import { FieldFilterType, IEntity, PropertyType } from "../types";
import { IDataLink, IEntityFilter, IPageLink, ITableLookup, IValueExpression } from "../models";

import { Binding } from "../bindings";
import { DataLinkBinding } from "../bindings/data-link";
import { GridOptionsBinding } from "../bindings/grid-options";
import { IBoolFieldBinding } from "../bindings/boolean";
import { IDateFieldBinding } from "../bindings/date";
import { IMediaFieldBinding } from "../bindings/media";
import { IMediaSetFieldBinding } from "../bindings/media-set";
import { INumberFieldBinding } from "../bindings/number";
import { IStringFieldBinding } from "../bindings/string";
import { IUrlFieldBinding } from "../bindings/url";
import { PageLinkBinding } from "../bindings/page-link";
import { PageLinkHelper } from "../helpers";
import { TableLookupBinding } from "../bindings/table-lookup";

export const getProperties = ({ bindings, row, filters: pageFilters }: {
    bindings: Binding[], row: IEntity, filters: IEntityFilter[]
}) => {
    const properties: IEntity = {}

    bindings?.forEach(item => {
        switch (item.propType) {
            case PropertyType.PageLink:
                properties[item.propId] = getPageLinkProperty(item, pageFilters, row);
                break;
            case PropertyType.DataLink:
                properties[item.propId] = getDataLinkProperty(item, pageFilters, row);
                break;
            case PropertyType.TableLookup:
                properties[item.propId] = getTableLookupProperty(item, pageFilters, row);
                break;
            case PropertyType.GridOptions:
                properties[item.propId] = getGridOptionsProperty(item);
                break;
            case PropertyType.Dropdown:
                properties[item.propId] = item.value;
                break;
            case PropertyType.Enumeration:
                properties[item.propId] = item.value;
                break;
            default:
                if (item.type === "Field") {
                    properties[item.propId] = getFieldProperty(item, pageFilters, row);
                } else if (item.type === "Expression") {
                    properties[item.propId] = getExpressionValue(item.value, pageFilters, row)
                } else if (item.type === "Const") {
                    properties[item.propId] = item.value;
                }
        }
    });

    return properties;
}

const getFieldValue = (name: string, row: IEntity, filters: IEntityFilter[]) => {
    let value = row ? row[name] : undefined
    if (value !== undefined) {
        return value;
    }

    const filter = filters.find(p => p.field === name && p.type === FieldFilterType.Const)
    if (filter) {
        return filter.value
    }
}

const getStringExprValue = (expression: IValueExpression, row: IEntity, filters: IEntityFilter[]) => {
    let queryResult = expression.query;

    try {
        expression.parameters.forEach(param => {
            const value = param.type === "Field" ?
                getFieldValue(param.value, row, filters) :
                param.value;
            queryResult = queryResult.replaceAll(`{${param.name}}`, String(value));
        });

        return queryResult;
    } catch (err) {
        console.error("expression", queryResult)
        console.error(err)
    }
}

const getBooleanExprValue = (expression: IValueExpression, row: IEntity, filters: IEntityFilter[]) => {
    let queryResult = expression.query;

    try {
        expression.parameters.forEach(param => {
            const value = param.type === "Field" ?
                getFieldValue(param.value, row, filters) : param.value;

            if (typeof value === "string")
                queryResult = queryResult.replaceAll(`{${param.name}}`, `"${value}"`)
            else
                queryResult = queryResult.replaceAll(`{${param.name}}`, String(value));
        });

        // eslint-disable-next-line
        return Boolean(eval(queryResult));
    } catch (err) {
        console.error("expression", queryResult)
        console.error(err)
    }
}

const getNumberExprValue = (expression: IValueExpression, row: IEntity, filters: IEntityFilter[]) => {
    let queryResult = expression.query;

    try {
        expression.parameters.forEach(param => {
            const value = param.type === "Field" ?
                getFieldValue(param.value, row, filters) : param.value;

            if (typeof value === "string")
                queryResult = queryResult.replaceAll(`{${param.name}}`, `"${value}"`)
            else
                queryResult = queryResult.replaceAll(`{${param.name}}`, String(value));
        });

        // eslint-disable-next-line
        return Number(eval(queryResult));
    } catch (err) {
        console.error("expression", queryResult)
        console.error(err)
    }
}

const getPageLinkProperty = (binding: PageLinkBinding, filters: IEntityFilter[], row: IEntity) => {
    filters = PageLinkHelper.resolveFilters(binding.value.filters || [], row, filters);
    let pageLink = { ...binding.value, filters };
    const systemId = (row && pageLink.systemId) ? row[pageLink.systemId] : undefined;
    return { ...pageLink, systemId } as IPageLink;
}

const getDataLinkProperty = (binding: DataLinkBinding, filters: IEntityFilter[], row: IEntity) => {
    filters = PageLinkHelper.resolveFilters(binding.value.filters || [], row, filters);
    let dataLink = { ...binding.value, filters: filters } as IDataLink;
    const systemId = (row && dataLink.systemId) ? row[dataLink.systemId] : undefined;
    return { ...dataLink, systemId } as IDataLink;
}

const getTableLookupProperty = (binding: TableLookupBinding, filters: IEntityFilter[], row: IEntity) => {
    const tableLookup = binding.value;
    filters = PageLinkHelper.resolveFilters(tableLookup.filters || [], row, filters);

    const idField = (binding.type === "Const") ?
        tableLookup.idField :
        tableLookup.idField ? row[tableLookup.idField] : undefined;

    const displayField = (binding.type === "Const") ?
        tableLookup.displayField :
        tableLookup.displayField ? row[tableLookup.displayField] : undefined;

    const tableId = (binding.type === "Const") ?
        tableLookup.tableId :
        tableLookup.tableId ? row[tableLookup.tableId] : '';

    return { tableId, idField, displayField, filters } as ITableLookup;
}

const getExpressionValue = (
    value: IValueExpression,
    filters: IEntityFilter[],
    row: IEntity
) => {

    switch (value.type) {
        case "String":
            return getStringExprValue(value, row, filters);
        case "Boolean":
            return getBooleanExprValue(value, row, filters);
        case "Number":
            return getNumberExprValue(value, row, filters);
        default:
            throw new Error(`Invalid binding expression value: ${JSON.stringify(value)}`)
    }
}

const getFieldProperty = (
    binding: IBoolFieldBinding | IDateFieldBinding | IMediaSetFieldBinding |
        IMediaFieldBinding | INumberFieldBinding | IStringFieldBinding |
        IUrlFieldBinding,
    filters: IEntityFilter[],
    row: IEntity,
) => {
    return binding.value ? getFieldValue(binding.value, row, filters) : undefined;
}

const getGridOptionsProperty = (binding: GridOptionsBinding) => {
    return binding.value;
}
